import JobPictureApi from '~/common/api/zeiterfassungServerApi/JobPictureApi';
import { Flex, Grid, GridItem, IconButton, Select } from '@chakra-ui/react';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { Text, Button } from '~/common/components/defaultUIComponents';
import { ArrowForwardIcon, ArrowBackIcon } from '@chakra-ui/icons';
import React, { createRef, useState, useEffect } from 'react';
import { formatName } from '~/common/utils/employeeUtils';
import { ListView } from '~/common/components/ListView';
import { NewImageListItem } from './NewImageListItem';
import useTranslate from '~/common/utils/translate';
import _ from 'lodash';

type FilterState = {
  employee?: number;
  project?: number;
};

export const NewImageList: React.FC = () => {
  const t = useTranslate();
  const [filterState, setFilterState] = useState<FilterState>({});
  const employeeRef = createRef<HTMLSelectElement>();
  const projectRef = createRef<HTMLSelectElement>();
  const [page, setPage] = useState(0);
  const [{ data: unacceptedPicturesCount }] =
    JobPictureApi.hooks.useUnacceptedPicturesCount({
      refetchInterval: 1000 * 60,
    });
  const [{ data: jobs, isLoading }, refetch] =
    JobPictureApi.hooks.useJobsWithUnacceptedPictures(page, filterState, {
      enabled: unacceptedPicturesCount && unacceptedPicturesCount.count > 0,
    });

  const handleClick = () => {
    setFilterState((prevState) => {
      const newFilter = { ...prevState };
      if (employeeRef.current && projectRef.current) {
        if (employeeRef.current.value && employeeRef.current.value !== '-1') {
          newFilter.employee = parseInt(employeeRef.current.value);
        } else {
          newFilter.employee = undefined;
        }
        if (projectRef.current.value && projectRef.current.value !== '-1') {
          newFilter.project = parseInt(projectRef.current.value);
        } else {
          newFilter.project = undefined;
        }
      }
      return newFilter;
    });
  };

  const resetFilters = () => {
    setFilterState({});
    if (employeeRef.current && projectRef.current) {
      employeeRef.current.value = '-1';
      projectRef.current.value = '-1';
    }
  };

  const [{ data: employees }] = EmployeeApi.hooks.useEmployeeQuery();
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery();

  const [prevUnacceptedCount, setPrevUnacceptedCount] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (
      unacceptedPicturesCount &&
      jobs &&
      prevUnacceptedCount !== null &&
      prevUnacceptedCount !== unacceptedPicturesCount.count &&
      !isLoading
    ) {
      void refetch();
    }
    setPrevUnacceptedCount(unacceptedPicturesCount?.count || null);
  }, [unacceptedPicturesCount]);

  return (
    <>
      <Flex direction="row" justify="space-between" mb={3}>
        <Flex gap={3}>
          <Select
            ref={employeeRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectEmployee')}</option>
            {_.orderBy(employees, [(e) => formatName(e)], ['asc'])?.map((e) => (
              <option key={e.id} value={e.id}>
                {formatName(e)}
              </option>
            ))}
          </Select>
          <Select
            ref={projectRef}
            style={{
              width: '145px',
            }}
          >
            <option value="-1">{t('selectProject')}</option>
            {_.orderBy(projects, ['name'], ['asc'])?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <Flex gap={3} alignItems="center">
            {(filterState?.project || filterState?.employee) && (
              <Text
                textDecoration="underline"
                cursor="pointer"
                color="green.600"
                style={{ whiteSpace: 'nowrap' }}
                onClick={resetFilters}
              >
                {t('resetFilter')}
              </Text>
            )}
            <Button onClick={handleClick}>{t('search')}</Button>
          </Flex>
        </Flex>
      </Flex>
      <Flex w="100%" direction="column" alignItems="center">
        <Flex gap={3} alignItems="center" mb={3}>
          <IconButton
            aria-label="previous"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(
                Math.min(
                  page + 1,
                  Math.floor((unacceptedPicturesCount?.count ?? 0) / 100),
                ),
              )
            }
          />
        </Flex>
        <ListView
          data={jobs}
          search=""
          searchFields={() => []}
          isLoading={isLoading}
          render={({ data }) => (
            <Grid templateColumns="repeat(3, 1fr)" gap={3}>
              {data.map((job) => (
                <GridItem key={job.id}>
                  <NewImageListItem job={job} />
                </GridItem>
              ))}
            </Grid>
          )}
        />
        <Flex gap={3} alignItems="center" mt={3}>
          <IconButton
            aria-label="previous"
            icon={<ArrowBackIcon />}
            onClick={() => setPage(Math.max(page - 1, 0))}
          />
          <Text size="lg">{page + 1}</Text>
          <IconButton
            aria-label="next"
            icon={<ArrowForwardIcon />}
            onClick={() =>
              setPage(
                Math.min(
                  page + 1,
                  Math.floor((unacceptedPicturesCount?.count ?? 0) / 100),
                ),
              )
            }
          />
        </Flex>
      </Flex>
    </>
  );
};
